import { Toolbar, alpha } from '@mui/material';

import { tss } from 'tss-react/mui';

import type { Dispatch, SetStateAction } from 'react';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import type { FileManagerUploadingState } from '@/modules/files/components/file-manager/FileManagerUploadingBar';

import type { OwnerPermisson } from '../../hooks/useFilePermissions';

import { FileManagerSearchBar } from './toolbar/FileManagerSearchBar';
import { FileManagerBreadcrumbsBar } from './toolbar/FileManagerBreadcrumbsBar';
import { isRestrictedSegment } from './toolbar/toolbarUtils';
import type { Segment } from './toolbar/breadcrumbs-bar/FileManagerBreadcrumbsBarSegment';

export const FILE_MANAGER_TOOLBAR_BREADCRUMB_FRAGMENT = graphql(`
  fragment fileManagerToolbarBreadcrumbFields on File {
    ...fileFields
    owner {
      ... on Group {
        _id
        name
      }

      ... on Service {
        _id
        name
      }
    }
  }
`);

const FILE_MANAGER_TOOLBAR_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileManagerToolbarFilesForOwnerFields on FilesForOwner {
    ...fileManagerBreadcrumbsBarFilesForOwnerFields
  }
`);

interface Props {
  filesForOwner:
    | DocumentType<typeof FILE_MANAGER_TOOLBAR_FILES_FOR_OWNER_FRAGMENT>
    | null
    | undefined;
  breadcrumbs: Array<
    DocumentType<typeof FILE_MANAGER_TOOLBAR_BREADCRUMB_FRAGMENT>
  >;
  segments: Segment[];
  ownerPermissions: OwnerPermisson[];
  searchText: string;
  searchMode: boolean;
  setSearchText: Dispatch<SetStateAction<string>>;
  setSearchMode: Dispatch<SetStateAction<boolean>>;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
}

const useStyles = tss.create(({ theme }) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'stretch',
    flexDirection: 'column',
    overflow: 'hidden',
  },

  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  restrictedBanner: {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    padding: `${theme.spacing(1, 3)}`,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

export const FileManagerToolbar = ({
  filesForOwner,
  breadcrumbs,
  segments,
  ownerPermissions,
  searchText,
  searchMode,
  setSearchMode,
  setSearchText,
  setUploadingState,
}: Props) => {
  const { classes } = useStyles();

  const { owner } = filesForOwner ?? {};

  const firstRestrictedSegment = breadcrumbs.find((crumb) =>
    isRestrictedSegment(
      { fileId: crumb._id, ownerId: crumb.owner?._id ?? null },
      owner?._id,
      owner?.__typename,
    ),
  );

  return (
    <div className={classes.root}>
      <Toolbar className={classes.toolbar}>
        {searchMode ? (
          <FileManagerSearchBar
            searchText={searchText}
            setSearchText={setSearchText}
            hideSearch={() => setSearchMode(false)}
          />
        ) : (
          <FileManagerBreadcrumbsBar
            segments={segments}
            ownerPermissions={ownerPermissions}
            filesForOwner={filesForOwner}
            setSearchMode={setSearchMode}
            setUploadingState={setUploadingState}
          />
        )}
      </Toolbar>
      {!searchMode && firstRestrictedSegment && (
        <div className={classes.restrictedBanner}>
          This folder is only visible to members of{' '}
          <strong>{firstRestrictedSegment?.owner?.name ?? ''}</strong>
        </div>
      )}
    </div>
  );
};
